import React from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import RouteSchemaMarkup from '../../components/RouteSchemaMarkup';
import nagpurBanner from '../../assets/images/city-banners/nagpur.png'
import usePageMetadata, { NAGPUR_KEYWORDS, DEFAULT_KEYWORDS } from '../../hooks/usePageMetadata';

const NagpurPage: React.FC = () => {
  // List of colleges where CTRL+P is available in Nagpur
  const availableColleges = [
    {
      name: "Indian Institute of Information Technology (IIIT)",
      address: "Survey No. 140,141/1 behind Br. Sheshrao Wankhade Shetkari Sahkari Soot Girni, Village - Waranga, PO - Dongargaon, Nagpur, Maharashtra 441108"
    },
    {
      name: "Maharashtra National Law University (MNLU)",
      address: "Near Village Waranga, PO: Dongargaon (Butibori), Nagpur, Maharashtra 441108"
    },
    {
      name: "Yeshwantrao Chavan College of Engineering (YCCE)",
      address: "Hingna Road, Wanadongri, Nagpur, Maharashtra 441110"
    },
    {
      name: "All India Institute of Medical Sciences (AIIMS)",
      address: "Plot No.2, Sector-20, MIHAN, Nagpur, Maharashtra 441108"
    },
    {
      name: "Indian Institute of Management (IIM)",
      address: "MIHAN Nagpur, Maharashtra 441108"
    },
    {
      name: "G.H. Raisoni College of Engineering",
      address: "CRPF Gate No. 3, Digdoh Hills, Hingna Road, Nagpur, Maharashtra 440016"
    }
  ];

  // Use the metadata hook instead of inline code
  usePageMetadata({
    title: "Printing services in nagpur | CTRL+P Print shop",
    description: "Nearest Photocopy shop in Nagpur .Automated printing by CTRL+P in Nagpur",
    keywords: `${NAGPUR_KEYWORDS}, ${DEFAULT_KEYWORDS}`
  });

  return (
    <>
      <Header />
      <RouteSchemaMarkup />
      <div className="bg-white">
        {/* Hero Section */}
        <div className="relative bg-gray-900 text-white">
          <div className="absolute inset-0 overflow-hidden">
            <img 
              src={nagpurBanner}
              alt="Nagpur City" 
              className="w-full h-full object-cover opacity-40"
              onError={(e) => {
                e.currentTarget.src = "https://images.unsplash.com/photo-1623053847057-99484b7cd64b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80";
              }}
            />
          </div>
          <div className="relative container mx-auto px-4 py-24 md:py-32">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">CTRL+P in Nagpur</h1>
            <p className="text-xl md:text-2xl max-w-2xl leading-relaxed text-gray-100">Fast, reliable, and convenient printing services across the Orange City</p>
          </div>
        </div>

        {/* About Nagpur Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-800">About Nagpur</h2>
            <div className="prose lg:prose-lg text-gray-700 leading-relaxed tracking-wide">
              <p className="mb-4">
                Known as the "Orange City" of India, Nagpur is the third largest city in Maharashtra and the winter capital of the state. 
                Located at the geographical center of India, Nagpur is a major commercial and political hub.
              </p>
              <p className="mb-4">
                The city is renowned for its educational institutions, with numerous colleges and universities making it an educational 
                center of Central India. Nagpur is home to several prestigious institutions including VNIT, AIIMS, IIM, MNLU, IIIT and many engineering 
                and medical colleges.
              </p>
              <p className="mb-4">
                With a vibrant student population and growing educational infrastructure, Nagpur presented the perfect opportunity for 
                CTRL+P to expand its printing services to cater to the academic and professional printing needs of students and faculty.
              </p>
            </div>
          </div>
        </div>

        {/* CTRL+P in Nagpur Section */}
        <div className="bg-gray-100 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-gray-800">CTRL+P in Nagpur</h2>
              <div className="prose lg:prose-lg text-gray-700 leading-relaxed tracking-wide">
                <p className="mb-4">
                  <strong className="text-blue-700">Nagpur was the first city where CTRL+P was launched.</strong> CTRL+P launched its services in Nagpur in 2025, recognizing the city's substantial student population and the growing 
                  need for convenient, high-quality printing services. Our mission is to make printing hassle-free for students and 
                  professionals alike.
                </p>
                <p className="mb-4">
                  We've strategically established our printing centers across major educational institutions in Nagpur, making it easy 
                  for students to access our services without leaving their campus. Our user-friendly online platform allows users to 
                  upload documents and pick them up from their nearest selected shop.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Available Colleges Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-800">Available at These Colleges</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {availableColleges.map((college, index) => (
                <div key={index} className="border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 bg-white flex flex-col h-full">
                  <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
                    <h3 className="font-bold text-lg text-blue-800 line-clamp-2">{college.name}</h3>
                  </div>
                  <div className="p-6 flex-grow flex flex-col">
                    <p className="text-gray-600 mb-4 flex-grow line-clamp-3">{college.address}</p>
                    <a 
                      href={`https://maps.google.com/?q=${encodeURIComponent(college.name + ', ' + college.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors mt-auto"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      View on Map
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-12 relative overflow-hidden">

          <div className="absolute top-0 left-0 w-24 h-24 bg-blue-50 rounded-full opacity-50 transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-blue-50 rounded-full opacity-50 transform translate-x-1/2 translate-y-1/2"></div>
          
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg p-8 border border-gray-100 relative z-10">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-50 rounded-bl-3xl z-0"></div>
              <div className="relative z-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800">Ready to Print in Nagpur?</h2>
                <p className="text-xl mb-6 leading-relaxed text-gray-600">
                  Upload your documents now and pick them up at your nearest CTRL+P print shop on campus.
                </p>
                <a 
                  href="/get-print" 
                  className="inline-block bg-blue-600 text-white font-bold py-3 px-10 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md"
                >
                  Start Printing Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default NagpurPage;
